import React, { useState } from "react";
import TopNav from "../Navbars/TopNav";
import Sidebar from "../Navbars/sidebar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import {
  useGetInternByid,
  useGetAllSupervisors,
  useAssignSupervisor,
} from "../../adapters/Requests";
import { useParams, useNavigate, Link } from "react-router-dom";
import { formatDuration } from "../../adapters/utils";
import useAuth from "../../hooks/useAuth";
import { Home, ChevronRight, ArrowLeft } from "lucide-react";

function AssignSupervisor() {
  const { id: internId } = useParams();
  const navigate = useNavigate();
  const { auth } = useAuth();

  const { data: internDataObj } = useGetInternByid(internId);
  const internData = internDataObj?.data || internDataObj;

  const { data: allSupervisors = [], isLoading: loadingAllSupervisors } =
    useGetAllSupervisors();
  const assignSupervisorMutation = useAssignSupervisor();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [supervisorSearch, setSupervisorSearch] = useState("");

  const currentSupervisorId =
    internData?.InternPlacements?.[0]?.supervising_officer_staff_id;
  const hasSupervisor = currentSupervisorId != null;
  const currentSupervisor = allSupervisors.find(
    (s) => s.staff_id === currentSupervisorId
  );
  const supervisorDisplayName = currentSupervisor
    ? `${currentSupervisor.first_name} ${currentSupervisor.last_name} (${currentSupervisor.staff_id})`
    : currentSupervisorId;

  const canAssign =
    auth?.permissions?.includes("assign_supervisor") ||
    auth?.roles?.includes("representative");

  const handleOpenAssignDialog = () => {
    setIsDialogOpen(true);
    setSupervisorSearch("");
  };

  const handleSelectSupervisor = async (supervisor) => {
    try {
      const payload = {
        staff_id: supervisor.staff_id,
        staff_name: `${supervisor.first_name} ${supervisor.last_name}`,
        staff_email: supervisor.email,
        business_unit: supervisor.business_unit.name,
        location: supervisor.location.name,
        role: "supervisor",
      };

      await assignSupervisorMutation.mutateAsync({ internId, payload });

      setIsDialogOpen(false);
      toast.success(hasSupervisor
        ? "Supervisor reassigned successfully!"
        : "Supervisor assigned successfully!");
      setTimeout(() => {
        navigate("/dashboard");
      }, 2000);
    } catch (error) {
      console.error(error);
      toast.error("Failed to assign: " + (error.response?.data?.message || error.message));
    }
  };

  const institutionName =
    internData?.application?.ApplicationsAcademicQualification?.institution
      ?.name ||
    internData?.application?.ApplicationNYSCDetails?.institution?.name ||
    "N/A";

  const duration =
    internData?.application?.Applicant?.InternshipProgram?.[0]
      ?.duration_in_months;
  const formattedDuration = formatDuration(duration);

  const filteredSupervisors = allSupervisors.filter((s) =>
    `${s.first_name} ${s.last_name}`
      .toLowerCase()
      .includes(supervisorSearch.toLowerCase())
  );

  return (
    <>
      {/* <TopNav /> */}
      <div className="flex overflow-hidden bg-slate-50 min-h-screen">
        {/* <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /> */}
        <div className="w-[80%] py-10 lg:py-14 mx-auto mt-10">
          <div className="flex items-center justify-between w-full mb-6">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Link
                to="/dashboard"
                className="flex items-center hover:text-green-600 transition-colors"
              >
                <Home size={16} />
              </Link>
              <ChevronRight size={16} className="text-gray-300" />
              <span className="font-semibold text-gray-900">
                Assign Supervisor
              </span>
            </div>

            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft size={16} /> Back
            </button>
          </div>

          <div className="bg-white rounded-xl shadow p-4 sm:p-7 dark:bg-neutral-900">
            <h1 className="text-center font-bold mb-4">
              {hasSupervisor
                ? "RE-ASSIGN INTERN"
                : "ASSIGN INTERN TO SUPERVISOR"}
            </h1>


            <div className="grid sm:grid-cols-12 gap-2 sm:gap-4 py-4 first:pt-0 last:pb-0 border-t first:border-transparent border-gray-200 dark:border-neutral-700 dark:first:border-transparent">
              {hasSupervisor && (
                <div className="sm:col-span-12 bg-yellow-50 p-2 rounded border border-yellow-200 mb-2">
                  <label className="text-sm font-medium text-yellow-800">
                    Current Supervisor:{" "}
                    <span className="font-bold text-gray-900">
                      {supervisorDisplayName}
                    </span>
                  </label>
                </div>
              )}

              <div className="sm:col-span-4">
                <label className="inline-block text-sm font-medium text-gray-900 mt-2.5 dark:text-neutral-500">
                  Name of Student :{" "}
                  <span className="font-bold">
                    {`${internData?.first_name || ""} ${internData?.last_name || ""
                      }`}
                  </span>
                </label>
              </div>

              <div className="sm:col-span-4">
                <label className="inline-block text-sm font-medium text-gray-900 mt-2.5 dark:text-neutral-500">
                  School ID Number :{" "}
                  <span className="font-bold">
                    {internData?.application?.ApplicationsAcademicQualification
                      ?.matric_number || "N/A"}
                  </span>
                </label>
              </div>

              <div className="sm:col-span-4">
                <label className="inline-block text-sm font-medium text-gray-900 mt-2.5 dark:text-neutral-500">
                  Institution :{" "}
                  <span className="font-bold">{institutionName}</span>
                </label>
              </div>

              <div className="sm:col-span-4">
                <label className="inline-block text-sm font-medium text-gray-900 mt-2.5 dark:text-neutral-500">
                  Duration of Attachment :{" "}
                  <span className="font-bold"> {formattedDuration} </span>
                </label>
              </div>

              <div className="sm:col-span-4">
                <label className="inline-block text-sm font-medium text-gray-900 mt-2.5 dark:text-neutral-500">
                  Department :{" "}
                  <span className="font-bold">
                    {`${internData?.application?.Applicant?.InternshipProgram?.[0]
                        ?.department || ""
                      }`}
                  </span>
                </label>
              </div>
            </div>
          </div>

          {canAssign ? (
            <div className="text-center py-6">
              <button
                onClick={handleOpenAssignDialog}
                disabled={!internData}
                className="py-3 px-4 inline-flex justify-center items-center gap-2 rounded-md border border-transparent font-semibold bg-green-500 text-white hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all text-sm dark:focus:ring-offset-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {hasSupervisor ? "Re-Assign Supervisor" : "Assign Supervisor"}
              </button>

              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="max-w-md w-full">
                  <DialogHeader>
                    <DialogTitle>Select a Supervisor</DialogTitle>
                  </DialogHeader>

                  <div className="my-2">
                    <Input
                      placeholder="Search supervisor name..."
                      value={supervisorSearch}
                      onChange={(e) => setSupervisorSearch(e.target.value)}
                    />
                  </div>

                  <div className="mt-4 max-h-[60vh] overflow-y-auto pr-2">
                    {loadingAllSupervisors ? (
                      <div className="text-center py-4 text-gray-500">
                        Loading staff...
                      </div>
                    ) : filteredSupervisors.length > 0 ? (
                      filteredSupervisors.map((person) => (
                        <div
                          key={person.id}
                          className="p-4 mb-4 transition-colors border rounded-md cursor-pointer hover:bg-green-50 hover:border-green-200"
                          onClick={() => handleSelectSupervisor(person)}
                        >
                          <h3 className="font-semibold text-gray-800">
                            {person.first_name} {person.last_name}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {person.business_unit.name}
                          </p>
                          <p className="text-xs text-gray-400">
                            {person.email}
                          </p>
                        </div>
                      ))
                    ) : (
                      <div className="py-6 text-center text-gray-500">
                        No supervisors found in your department.
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          ) : (
            <h1 className="text-center font-bold mt-6 text-green-400">
              VIEW ONLY
            </h1>
          )}
        </div>
      </div>
    </>
  );
}

export default AssignSupervisor;
